import React from "react";
import type { NoteName } from "../../lib/guitarEngine";

export interface ChordPracticeInspectorPanelProps {
  targetNoteNames: string[];
  detectionMode: "chord" | "arpeggio";
  setDetectionMode: (m: "chord" | "arpeggio") => void;
  arpeggioPhase: "notes" | "chord" | "done";
  setArpeggioPhase: (p: "notes" | "chord" | "done") => void;
  arpeggioIndex: number;
  setArpeggioIndex: (i: number) => void;
  arpeggioResults: boolean[];
  setArpeggioResults: (r: boolean[]) => void;
  setLastResult: (r: null) => void;
  engineState: { isLoading: boolean };
  isRecording: boolean;
  isArpeggioRecording: boolean;
  handleListenChord: () => void;
  handleArpeggioNote: () => void;
  handleArpeggioChord: () => void;
  lastResult: {
    matched: number[];
    missing: number[];
    extra: number[];
    score: number;
  } | null;
  notes: readonly NoteName[];
}

function ChordDetectionResultSummary({
  lastResult,
  notes,
}: {
  lastResult: NonNullable<ChordPracticeInspectorPanelProps["lastResult"]>;
  notes: readonly NoteName[];
}) {
  return (
    <div className="space-y-1.5 bg-white/[0.07] rounded-lg p-2.5 border border-white/10">
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-white/15 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              lastResult.score >= 0.9 && lastResult.extra.length === 0
                ? "bg-green-500"
                : lastResult.score >= 0.6
                  ? "bg-amber-500"
                  : "bg-rose-500"
            }`}
            style={{ width: `${lastResult.score * 100}%` }}
          />
        </div>
        <span
          className={`text-xs font-sans font-bold ${
            lastResult.score >= 0.9 && lastResult.extra.length === 0
              ? "text-green-600"
              : lastResult.score >= 0.6
                ? "text-amber-600"
                : "text-rose-600"
          }`}
        >
          {(lastResult.score * 100).toFixed(0)}%
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {lastResult.matched.map((pc) => (
          <span key={pc} className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-sans font-medium">
            ✓ {notes[pc]}
          </span>
        ))}
        {lastResult.missing.map((pc) => (
          <span key={pc} className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-sans font-medium">
            ✗ {notes[pc]}
          </span>
        ))}
        {lastResult.extra.map((pc) => (
          <span key={pc} className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-sans font-medium">
            ? {notes[pc]}
          </span>
        ))}
      </div>
      {lastResult.score >= 0.9 && lastResult.extra.length === 0 && (
        <p className="text-xs font-sans font-bold text-green-600">¡Acorde perfecto!</p>
      )}
      {lastResult.score >= 0.9 && lastResult.extra.length > 0 && (
        <p className="text-xs font-sans font-bold text-amber-600">¡Casi! Sobran notas extra</p>
      )}
      {lastResult.score < 0.6 && (
        <p className="text-xs font-sans text-rose-600">Faltan notas del acorde</p>
      )}
    </div>
  );
}

export const ChordPracticeInspectorPanel: React.FC<ChordPracticeInspectorPanelProps> = ({
  targetNoteNames,
  detectionMode,
  setDetectionMode,
  arpeggioPhase,
  setArpeggioPhase,
  arpeggioIndex,
  setArpeggioIndex,
  arpeggioResults,
  setArpeggioResults,
  setLastResult,
  engineState,
  isRecording,
  isArpeggioRecording,
  handleListenChord,
  handleArpeggioNote,
  handleArpeggioChord,
  lastResult,
  notes,
}) => {
  const resetArpeggio = () => {
    setArpeggioPhase("notes");
    setArpeggioIndex(0);
    setArpeggioResults([]);
    setLastResult(null);
  };

  return (
    <div
      className="p-4 rounded-2xl bg-white"
      style={{ boxShadow: "0 4px 20px rgba(45,42,38,0.04)" }}
    >
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 font-sans mb-2">
        Práctica
      </h4>

      <div className="flex flex-wrap gap-1 mb-2.5">
        {targetNoteNames.map((n, i) => (
          <span
            key={n}
            className={`px-2 py-1 rounded-md text-xs font-sans font-bold ${
              detectionMode === "arpeggio" && arpeggioPhase === "notes" && i === arpeggioIndex
                ? "bg-[var(--bordona-accent)] text-white animate-pulse"
                : detectionMode === "arpeggio" && arpeggioResults[i]
                  ? "bg-green-500 text-white"
                  : detectionMode === "arpeggio" && arpeggioResults.length > i && !arpeggioResults[i]
                    ? "bg-rose-400 text-white"
                    : "bg-white/[0.07] text-slate-100 border border-white/10"
            }`}
          >
            {n}
          </span>
        ))}
      </div>

      <div className="flex gap-1 mb-2.5">
        <button
          type="button"
          onClick={() => {
            setDetectionMode("chord");
            resetArpeggio();
          }}
          className={`flex-1 py-1.5 rounded-md text-[10px] font-sans font-medium transition-all ${
            detectionMode === "chord"
              ? "bg-slate-800 text-white"
              : "bg-white/[0.07] text-slate-300 border border-white/10 hover:bg-white/10"
          }`}
        >
          Acorde completo
        </button>
        <button
          type="button"
          onClick={() => {
            setDetectionMode("arpeggio");
            resetArpeggio();
          }}
          className={`flex-1 py-1.5 rounded-md text-[10px] font-sans font-medium transition-all ${
            detectionMode === "arpeggio"
              ? "bg-slate-800 text-white"
              : "bg-white/[0.07] text-slate-300 border border-white/10 hover:bg-white/10"
          }`}
        >
          Arpegio
        </button>
      </div>

      {engineState.isLoading && (
        <p className="text-[10px] text-amber-600 font-sans mb-2 animate-pulse">Cargando modelo…</p>
      )}

      {detectionMode === "chord" && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={handleListenChord}
            disabled={isRecording || engineState.isLoading}
            className={`w-full py-2.5 rounded-md text-xs font-sans font-bold transition-all ${
              isRecording
                ? "bg-rose-500 text-white animate-pulse"
                : "bg-[var(--bordona-accent)] text-white hover:bg-[var(--bordona-accent-hover)] disabled:opacity-40"
            }`}
          >
            {isRecording ? "● Grabando 2.5s…" : "Escuchar acorde"}
          </button>
          {lastResult && <ChordDetectionResultSummary lastResult={lastResult} notes={notes} />}
        </div>
      )}

      {detectionMode === "arpeggio" && (
        <div className="space-y-2">
          {arpeggioPhase === "notes" && (
            <>
              <p className="text-[11px] font-sans text-slate-300">
                Nota {arpeggioIndex + 1} de {targetNoteNames.length}: toca{" "}
                <span className="font-bold text-[var(--bordona-accent)]">{targetNoteNames[arpeggioIndex]}</span>
              </p>
              <button
                type="button"
                onClick={handleArpeggioNote}
                disabled={isArpeggioRecording || engineState.isLoading}
                className={`w-full py-2.5 rounded-md text-xs font-sans font-bold transition-all ${
                  isArpeggioRecording
                    ? "bg-rose-500 text-white animate-pulse"
                    : "bg-[#06B6D4] text-white hover:bg-[#0891B2] disabled:opacity-40"
                }`}
              >
                {isArpeggioRecording ? "● Grabando 1.5s…" : `Tocar ${targetNoteNames[arpeggioIndex]}`}
              </button>
              <div className="flex gap-1">
                {targetNoteNames.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full ${
                      i < arpeggioResults.length
                        ? arpeggioResults[i]
                          ? "bg-green-500"
                          : "bg-rose-400"
                        : i === arpeggioIndex
                          ? "bg-[#06B6D4] animate-pulse"
                          : "bg-white/15"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
          {arpeggioPhase === "chord" && (
            <>
              <p className="text-[11px] font-sans text-slate-300">
                ¡Arpegio completado! Ahora toca el{" "}
                <span className="font-bold text-[var(--bordona-accent)]">acorde completo</span>.
              </p>
              <button
                type="button"
                onClick={handleArpeggioChord}
                disabled={isArpeggioRecording || engineState.isLoading}
                className={`w-full py-2.5 rounded-md text-xs font-sans font-bold transition-all ${
                  isArpeggioRecording
                    ? "bg-rose-500 text-white animate-pulse"
                    : "bg-[var(--bordona-accent)] text-white hover:bg-[var(--bordona-accent-hover)] disabled:opacity-40"
                }`}
              >
                {isArpeggioRecording ? "● Grabando 2.5s…" : "Escuchar acorde completo"}
              </button>
            </>
          )}
          {arpeggioPhase === "done" && lastResult && (
            <div className="space-y-1.5 bg-white/[0.07] rounded-lg p-2.5 border border-white/10">
              <p className="text-xs font-sans font-bold text-slate-100">Resultado final</p>
              <ChordDetectionResultSummary lastResult={lastResult} notes={notes} />
              <button
                type="button"
                onClick={resetArpeggio}
                className="w-full py-1.5 rounded-md text-[10px] font-sans font-medium bg-white/[0.07] text-slate-300 border border-white/10 hover:bg-white/10 transition-all"
              >
                Reintentar arpegio
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
